// Libs
import React, { useMemo } from 'react';

// Constants
import {
  NOTIFICATIONS_ENTITY_TYPE,
  ROUTE_ORDERS,
  ROUTE_QUOTES,
  TYPE_ORIGIN,
} from '@/common/constants';
// Contexts
import { useAuth } from '@/common/contexts/AuthContext';
// Assets
import { ReactComponent as OrderIcon } from '@/ebat/assets/icons/order.svg';
import { ReactComponent as QuoteIcon } from '@/ebat/assets/icons/quote.svg';
import { ReactComponent as ChronoLogo } from '@/ebat/assets/logos/chrono.svg';
import { ReactComponent as EbatLogo } from '@/ebat/assets/logos/ebat.svg';
import { ReactComponent as MateriauxLogo } from '@/materiaux/assets/logos/les-materiaux.svg';

const useNotification = ({
  className,
  disabled,
  entityExists,
  isLoading,
  type,
  typeOrigin,
}) => {
  const { user } = useAuth();

  /**
   * Generate icon notification depending to type
   * @type {component}
   */
  const notificationIcon = useMemo(() => {
    switch (type) {
      case NOTIFICATIONS_ENTITY_TYPE.ORDER:
        return <OrderIcon className="notification__icon" />;
      case NOTIFICATIONS_ENTITY_TYPE.QUOTE:
        return <QuoteIcon className="notification__icon" />;
      default:
        return null;
    }
  }, [type]);

  /**
   * Get origin of the notification and show according logo
   * @param {string} origin from data notification
   * @type {(function(string): (*|null))|*}
   */
  const notificationLogo = useMemo(() => {
    switch (typeOrigin) {
      case TYPE_ORIGIN.EB:
        return <EbatLogo alt="eBAT" className="img" />;
      case TYPE_ORIGIN.EBC:
        return <ChronoLogo alt="Chrono eBAT" className="img" />;
      case TYPE_ORIGIN.LM:
        return <MateriauxLogo alt="Les Matériaux Express" className="img" />;
      default:
        return null;
    }
  }, [typeOrigin]);

  /**
   * Returns the route according to the type
   * @type {string}
   */
  const notificationLink = useMemo(() => {
    switch (type) {
      case NOTIFICATIONS_ENTITY_TYPE.ORDER:
        return ROUTE_ORDERS;
      case NOTIFICATIONS_ENTITY_TYPE.QUOTE:
        return ROUTE_QUOTES;
      default:
        return null;
    }
  }, [type]);

  const isLink = useMemo(() => entityExists && notificationLink, [
    entityExists,
    notificationLink,
  ]);

  /**
   * Check if component is disabled
   * @type {boolean}
   */
  const isDisabled = useMemo(() => {
    const blocked = isLoading || disabled;

    return {
      loading: isLoading,
      disabled: blocked,
      order:
        blocked ||
        !user?.functionality_accesses?.orders ||
        !user?.functionality_accesses?.orders_order,
      quote:
        blocked ||
        !user?.functionality_accesses?.quotes ||
        !user?.functionality_accesses?.quotes_quote,
    };
  }, [disabled, isLoading, user]);

  /**
   * Generate classNames
   * @type {string}
   */
  const classNames = useMemo(
    () => ['notification', className].filter((val) => val).join(' '),
    [className],
  );

  return {
    classNames,
    isDisabled,
    isLink,
    notificationIcon,
    notificationLink,
    notificationLogo,
  };
};

export default useNotification;
